import {
  SelectProps,
  ComboboxItem,
  Select,
  Loader,
  Stack,
  Center,
  Text,
} from "@mantine/core"
import { useIntersection } from "@mantine/hooks"
import { UseInfiniteQueryResult } from "@tanstack/react-query"

export interface InfiniteSelectProps<TData>
  extends Omit<SelectProps, "data" | "scrollAreaProps" | "renderOption"> {
  queryResult: UseInfiniteQueryResult<TData>
  mapDataToOption: (data: TData | undefined) => ComboboxItem[] | undefined
}

export function InfiniteSelect<TData>({
  queryResult,
  mapDataToOption,
  ...props
}: InfiniteSelectProps<TData>) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { ref, entry } = useIntersection({
    root: containerRef.current,
    threshold: 1,
  })
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = queryResult

  const options = React.useMemo<ComboboxItem[] | undefined>(
    () => mapDataToOption(data),
    [data]
  )

  React.useEffect(() => {
    entry?.isIntersecting &&
      !isFetchingNextPage &&
      hasNextPage &&
      fetchNextPage()
  }, [entry])

  return (
    <Select
      data={options}
      scrollAreaProps={{
        viewportRef: containerRef,
        scrollbars: "y",
        w: "100%",
        styles: { content: { width: "100%" } },
      }}
      renderOption={(item) => {
        const isLastOption =
          item.option.value === options?.[options?.length - 1]?.value

        return (
          <Stack w={"100%"}>
            <Text truncate ref={isLastOption ? ref : undefined}>
              {item.option.label}
            </Text>
            {isLastOption && isFetchingNextPage && (
              <Center style={{ justifyContent: "center", flex: 1 }}>
                <Loader size={"xs"} />
              </Center>
            )}
          </Stack>
        )
      }}
      {...props}
    />
  )
}
